import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Download,
  Mail,
  Printer,
  Trash2,
  X,
  Plus,
  ExternalLink,
  Phone,
  Pencil,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { printPdf } from '@/lib/printPdf';
import { useAuthContext } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { formatCents, formatDate, todayISO } from '@/lib/utils';
import AttachmentsPanel from '@/components/AttachmentsPanel';
import EmailPdfModal from '@/components/EmailPdfModal';

// ─── Types ───────────────────────────────────────────────────────────────────

type ReturnStatus = 'open' | 'refunded';

interface ReturnItem {
  id: number;
  item_id: number | null;
  description: string;
  qty: number;
  unit_price: number;
  line_total: number;
}

interface Refund {
  id: number;
  amount: number;
  date: string;
  notes: string | null;
  created_by: number | null;
  created_at: string;
}

interface ReturnDetail {
  id: number;
  number: string;
  estimate_id: number;
  customer_id: number;
  date: string;
  status: ReturnStatus;
  total: number;
  notes: string | null;
}

interface ReturnDetailResponse {
  data: {
    return: ReturnDetail & { store_credit_issued: number };
    items: ReturnItem[];
    customer: {
      id: number;
      name: string;
      contact_person: string | null;
      phone: string | null;
      email: string | null;
      address: string | null;
    };
    estimate: { id: number; number: string };
    refunds: Refund[];
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function returnStatusBadge(status: ReturnStatus) {
  if (status === 'refunded') return <Badge variant="green">Refunded</Badge>;
  return <Badge variant="amber">Open</Badge>;
}

// ─── Record Refund Modal ──────────────────────────────────────────────────────

interface RecordRefundModalProps {
  returnId: number;
  balanceCents: number;
  onClose: () => void;
  onSaved: () => void;
}

function RecordRefundModal({ returnId, balanceCents, onClose, onSaved }: RecordRefundModalProps) {
  const prefill = (Math.max(balanceCents, 0) / 100).toFixed(2);
  const [amountStr, setAmountStr] = useState(prefill);
  const [date, setDate] = useState(todayISO());
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const amount = Math.round((parseFloat(amountStr) || 0) * 100);
    if (amount <= 0) {
      setError('Amount must be greater than zero.');
      return;
    }
    setLoading(true);
    try {
      await apiFetch(`/api/returns/${returnId}/refunds`, {
        method: 'POST',
        body: JSON.stringify({ amount, date, notes: notes.trim() || undefined }),
      });
      toast.success('Refund recorded');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Record Refund</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="ref-amount">
                Amount ($) <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                  $
                </span>
                <Input
                  id="ref-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={amountStr}
                  onChange={(e) => setAmountStr(e.target.value)}
                  className="pl-7 tabular-nums"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ref-date">Date</Label>
              <Input
                id="ref-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ref-notes">Notes (optional)</Label>
            <Input
              id="ref-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Cash refund"
              disabled={loading}
            />
          </div>
          {balanceCents > 0 && (
            <p className="text-xs text-muted-foreground">
              Balance to refund:{' '}
              <span className="font-medium tabular-nums">{formatCents(balanceCents)}</span>
            </p>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving…' : 'Record Refund'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Edit Refund Modal ────────────────────────────────────────────────────────

interface EditRefundModalProps {
  refund: Refund;
  onClose: () => void;
  onSaved: () => void;
}

function EditRefundModal({ refund, onClose, onSaved }: EditRefundModalProps) {
  const [amountStr, setAmountStr] = useState(String((refund.amount / 100).toFixed(2)));
  const [date, setDate] = useState(refund.date.slice(0, 10));
  const [notes, setNotes] = useState(refund.notes ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const amount = Math.round((parseFloat(amountStr) || 0) * 100);
    if (amount <= 0) {
      setError('Amount must be greater than zero.');
      return;
    }
    setLoading(true);
    try {
      await apiFetch(`/api/refunds/${refund.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ amount, date, notes: notes.trim() || undefined }),
      });
      toast.success('Refund updated');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Edit Refund</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="er-amount">
                Amount ($) <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                  $
                </span>
                <Input
                  id="er-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={amountStr}
                  onChange={(e) => setAmountStr(e.target.value)}
                  className="pl-7 tabular-nums"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="er-date">Date</Label>
              <Input
                id="er-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="er-notes">Notes (optional)</Label>
            <Input
              id="er-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Cash refund"
              disabled={loading}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Issue Store Credit Modal ─────────────────────────────────────────────────

interface IssueStoreCreditModalProps {
  returnId: number;
  estimateId: number;
  customerId: number;
  maxCents: number;
  onClose: () => void;
  onSaved: () => void;
}

function IssueStoreCreditModal({
  returnId,
  estimateId,
  customerId,
  maxCents,
  onClose,
  onSaved,
}: IssueStoreCreditModalProps) {
  const queryClient = useQueryClient();
  const prefill = (Math.max(maxCents, 0) / 100).toFixed(2);
  const [amountStr, setAmountStr] = useState(prefill);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const amount = Math.round((parseFloat(amountStr) || 0) * 100);
    if (amount <= 0) {
      setError('Amount must be greater than zero.');
      return;
    }
    setLoading(true);
    try {
      await apiFetch(`/api/returns/${returnId}/store-credit`, {
        method: 'POST',
        body: JSON.stringify({ amount, notes: notes.trim() || undefined }),
      });
      toast.success('Store credit issued');
      queryClient.invalidateQueries({ queryKey: ['customer-ledger', String(customerId)] });
      queryClient.invalidateQueries({ queryKey: ['estimate', String(estimateId)] });
      onSaved();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong.';
      toast.error(msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Issue Store Credit</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="sc-amount">
              Amount ($) <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                $
              </span>
              <Input
                id="sc-amount"
                type="number"
                min="0.01"
                max={(Math.max(maxCents, 0) / 100).toFixed(2)}
                step="0.01"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                className="pl-7 tabular-nums"
                required
                disabled={loading}
              />
            </div>
            {maxCents > 0 && (
              <p className="text-xs text-muted-foreground">
                Balance to settle:{' '}
                <span className="font-medium tabular-nums">{formatCents(maxCents)}</span>
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sc-notes">Notes (optional)</Label>
            <Input
              id="sc-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Store credit in lieu of cash refund"
              disabled={loading}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Issuing…' : 'Issue Store Credit'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Confirm Delete Dialog ────────────────────────────────────────────────────

interface ConfirmDeleteProps {
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

function ConfirmDeleteDialog({ title, message, onConfirm, onCancel, loading }: ConfirmDeleteProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">{message}</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={onConfirm} disabled={loading}>
              {loading ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ReturnDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Modal state ──
  const [recordRefundOpen, setRecordRefundOpen] = useState(false);
  const [issueStoreCreditOpen, setIssueStoreCreditOpen] = useState(false);
  const [editRefund, setEditRefund] = useState<Refund | null>(null);
  const [deleteRefundId, setDeleteRefundId] = useState<number | null>(null);
  const [deletingRefund, setDeletingRefund] = useState(false);
  const [deleteReturnOpen, setDeleteReturnOpen] = useState(false);
  const [deletingReturn, setDeletingReturn] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['return', id],
    queryFn: () => apiFetch<ReturnDetailResponse>(`/api/returns/${id}`),
    enabled: !!id,
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['return', id] });
    queryClient.invalidateQueries({ queryKey: ['returns'] });
    if (data?.data.return.estimate_id) {
      queryClient.invalidateQueries({
        queryKey: ['estimate', String(data.data.return.estimate_id)],
      });
    }
  }

  // ── Delete return ──
  async function handleDeleteReturn() {
    setDeletingReturn(true);
    try {
      await apiFetch(`/api/returns/${id}`, { method: 'DELETE' });
      toast.success('Return deleted');
      queryClient.invalidateQueries({ queryKey: ['returns'] });
      if (data?.data.return.estimate_id) {
        queryClient.invalidateQueries({
          queryKey: ['estimate', String(data.data.return.estimate_id)],
        });
        queryClient.invalidateQueries({ queryKey: ['estimates'] });
      }
      navigate('/returns');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete return');
    } finally {
      setDeletingReturn(false);
      setDeleteReturnOpen(false);
    }
  }

  // ── Delete refund ──
  async function handleDeleteRefund(refundId: number) {
    setDeletingRefund(true);
    try {
      await apiFetch(`/api/refunds/${refundId}`, { method: 'DELETE' });
      toast.success('Refund deleted');
      invalidate();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete refund');
    } finally {
      setDeletingRefund(false);
      setDeleteRefundId(null);
    }
  }

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (error || !data?.data.return) {
    return (
      <div className="p-6">
        <Link
          to="/returns"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Returns
        </Link>
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Return not found.'}
        </div>
      </div>
    );
  }

  const { return: sr, items, customer, estimate, refunds } = data.data;
  const totalRefunded = refunds.reduce((sum, r) => sum + r.amount, 0);
  const storeCreditIssued = sr.store_credit_issued ?? 0;
  const balanceCents = sr.total - totalRefunded - storeCreditIssued;

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* ── Back nav ── */}
      <Link
        to="/returns"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Returns
      </Link>

      {/* ── Body: 60/40 grid — header + returned items in the 60% main column ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Main column (60%) */}
        <div className="lg:col-span-3 space-y-6">
          {/* ── Header card ── */}
          <Card>
            <div className="px-6 py-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                {/* Left: number, status, customer, estimate, date */}
                <div className="space-y-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-2xl font-bold tracking-tight tabular-nums">{sr.number}</h1>
                    {returnStatusBadge(sr.status)}
                    {isAdmin && sr.status === 'open' && (
                      <Link to={`/returns/${sr.id}/edit`}>
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
                          <Pencil className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                      </Link>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm">
                    <Link
                      to={`/customers/${customer.id}`}
                      className="font-medium text-foreground hover:underline inline-flex items-center gap-1"
                    >
                      {customer.name}
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </Link>
                    {customer.phone && (
                      <>
                        <span className="hidden sm:inline text-muted-foreground">·</span>
                        <a
                          href={`tel:${customer.phone}`}
                          className="text-muted-foreground hover:text-foreground hover:underline inline-flex items-center gap-1"
                        >
                          <Phone className="h-3 w-3" />
                          {customer.phone}
                        </a>
                      </>
                    )}
                    <span className="hidden sm:inline text-muted-foreground">·</span>
                    <span className="text-muted-foreground">
                      Against Estimate:{' '}
                      <Link
                        to={`/estimates/${estimate.id}`}
                        className="font-medium text-foreground hover:underline"
                      >
                        {estimate.number}
                      </Link>
                    </span>
                    <span className="hidden sm:inline text-muted-foreground">·</span>
                    <span className="text-muted-foreground">{formatDate(sr.date)}</span>
                  </div>
                  {sr.notes && (
                    <p className="text-sm text-muted-foreground max-w-prose whitespace-pre-line">
                      {sr.notes}
                    </p>
                  )}
                </div>

                {/* Right: action buttons */}
                <div className="flex items-center gap-2 flex-wrap shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => printPdf(`/api/returns/${id}/pdf`)}
                  >
                    <Printer className="h-3.5 w-3.5 mr-1.5" />
                    Print
                  </Button>
                  <a
                    href={`/api/returns/${id}/pdf?download=1`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm font-medium hover:bg-muted transition-colors"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download PDF
                  </a>
                  <Button variant="outline" size="sm" onClick={() => setEmailModalOpen(true)}>
                    <Mail className="h-3.5 w-3.5 mr-1.5" />
                    Email
                  </Button>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                      onClick={() => setDeleteReturnOpen(true)}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* ── Returned items + totals ── */}
          <Card>
            <div className="px-4 py-3 border-b bg-muted/30">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Returned Items
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/20">
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground w-8">
                      #
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                      Description
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-20">
                      Qty
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-28">
                      Unit Price
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-28">
                      Line Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <tr
                      key={item.id}
                      className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                    >
                      <td className="px-3 py-2.5 text-xs text-muted-foreground text-center">
                        {i + 1}
                      </td>
                      <td className="px-3 py-2.5 text-sm font-medium">
                        {item.item_id != null ? (
                          <Link
                            to={`/items/${item.item_id}`}
                            className="inline-block hover:underline hover:text-primary transition-colors"
                          >
                            {item.description}
                          </Link>
                        ) : (
                          item.description
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-sm">{item.qty}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-sm">
                        {formatCents(item.unit_price)}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium">
                        {formatCents(item.line_total)}
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-8 text-center text-sm text-muted-foreground"
                      >
                        No line items.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totals block */}
            <div className="px-4 py-4 border-t bg-muted/10">
              <div className="ml-auto max-w-xs space-y-1.5">
                <div className="flex items-center justify-between pt-2 border-t font-semibold">
                  <span>Return Total</span>
                  <span className="tabular-nums text-base">{formatCents(sr.total)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Total Refunded (cash)</span>
                  <span className="tabular-nums text-green-700 dark:text-green-400">
                    {formatCents(totalRefunded)}
                  </span>
                </div>
                {storeCreditIssued > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Store Credit Issued</span>
                    <span className="tabular-nums text-blue-700 dark:text-blue-400">
                      {formatCents(storeCreditIssued)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm font-medium pt-1 border-t">
                  <span>Balance to Settle</span>
                  <span
                    className={`tabular-nums ${balanceCents > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}
                  >
                    {formatCents(Math.max(balanceCents, 0))}
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* ── Refunds section ── */}
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Refunds
                </h2>
                {balanceCents > 0 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIssueStoreCreditOpen(true)}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1.5" />
                      Issue Store Credit
                    </Button>
                    <Button size="sm" onClick={() => setRecordRefundOpen(true)}>
                      <Plus className="h-3.5 w-3.5 mr-1.5" />
                      Record Refund
                    </Button>
                  </div>
                )}
              </div>

              {refunds.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No refunds recorded yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/20">
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                          Date
                        </th>
                        <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">
                          Amount
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                          Notes
                        </th>
                        {isAdmin && <th className="px-3 py-2.5 w-20" />}
                      </tr>
                    </thead>
                    <tbody>
                      {refunds.map((ref) => (
                        <tr
                          key={ref.id}
                          className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                        >
                          <td className="px-3 py-2.5 tabular-nums text-sm">
                            {formatDate(ref.date)}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium text-green-700 dark:text-green-400">
                            {formatCents(ref.amount)}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-muted-foreground">
                            {ref.notes ?? '—'}
                          </td>
                          {isAdmin && (
                            <td className="px-3 py-2.5">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  type="button"
                                  onClick={() => setEditRefund(ref)}
                                  className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                  title="Edit refund"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeleteRefundId(ref.id)}
                                  className="rounded p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                  title="Delete refund"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right sidebar (40%) */}
        <aside className="lg:col-span-2 space-y-6">
          {/* ── Parent estimate ── */}
          <Card>
            <CardContent className="p-0">
              <div className="px-4 py-3 border-b">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Against Estimate
                </h2>
              </div>
              <div className="px-4 py-4">
                <Link
                  to={`/estimates/${estimate.id}`}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:underline"
                >
                  {estimate.number}
                  <ExternalLink className="h-3 w-3 text-muted-foreground" />
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* ── Attachments & Notes ── */}
          <AttachmentsPanel module="sales_return" recordId={Number(id)} />
        </aside>
      </div>

      {/* ── Modals ── */}
      {recordRefundOpen && (
        <RecordRefundModal
          returnId={sr.id}
          balanceCents={balanceCents}
          onClose={() => setRecordRefundOpen(false)}
          onSaved={invalidate}
        />
      )}

      {issueStoreCreditOpen && (
        <IssueStoreCreditModal
          returnId={sr.id}
          estimateId={sr.estimate_id}
          customerId={sr.customer_id}
          maxCents={Math.max(balanceCents, 0)}
          onClose={() => setIssueStoreCreditOpen(false)}
          onSaved={invalidate}
        />
      )}

      {editRefund && (
        <EditRefundModal
          refund={editRefund}
          onClose={() => setEditRefund(null)}
          onSaved={invalidate}
        />
      )}

      {deleteRefundId !== null && (
        <ConfirmDeleteDialog
          title="Delete Refund"
          message="Are you sure you want to delete this refund? This action cannot be undone."
          onConfirm={() => handleDeleteRefund(deleteRefundId)}
          onCancel={() => setDeleteRefundId(null)}
          loading={deletingRefund}
        />
      )}

      {deleteReturnOpen && (
        <ConfirmDeleteDialog
          title="Delete Return"
          message={`Are you sure you want to delete return ${sr.number}? This reverses stock and removes refunds. This cannot be undone.`}
          onConfirm={handleDeleteReturn}
          onCancel={() => setDeleteReturnOpen(false)}
          loading={deletingReturn}
        />
      )}

      <EmailPdfModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        endpoint={`/api/returns/${id}/email`}
        defaultTo={customer.email ?? ''}
        defaultSubject={`Sales Return ${sr.number}`}
        pdfHref={`/api/returns/${id}/pdf`}
      />
    </div>
  );
}
